import { Link } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { ApiKeyDisplay } from '@/components/dashboard/ApiKeyDisplay'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface RecentEvent {
  id: string
  event_type: string
  room_name: string
  participant_id: string | null
  occurred_at: string
}

interface Application {
  id: string
  name: string
  app_id: string | null
  active: boolean
  created_at: string
}

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  application: Application
  new_key?: string | null
  recent_events: RecentEvent[]
}

export default function Show({ user, organization, application, new_key, recent_events }: Props) {
  const rotateUrl = `/dashboard/applications/${application.id}/rotate_key`

  return (
    <AppShell user={user} organization={organization}>
      <div className="space-y-8">
        <PageHeader
          eyebrow={`Application · created ${new Date(application.created_at).toLocaleDateString()}`}
          title={application.name}
          actions={
            <Badge variant={application.active ? 'default' : 'secondary'}>
              {application.active ? 'active' : 'inactive'}
            </Badge>
          }
        />

        <Tabs defaultValue={new_key ? 'api-keys' : 'overview'}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="api-keys">API Keys</TabsTrigger>
            <TabsTrigger value="events">Recent Events</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <section className="surface-card p-6">
              <h2 className="font-heading text-base font-semibold tracking-tight">
                Application details
              </h2>
              <dl className="mt-4 divide-y divide-foreground/[0.06] text-sm">
                {[
                  { label: 'Name', value: <span className="font-medium">{application.name}</span> },
                  { label: 'App ID', value: <span className="font-mono text-xs">{application.app_id ?? '—'}</span> },
                  {
                    label: 'Status',
                    value: (
                      <Badge variant={application.active ? 'default' : 'secondary'} className="text-xs">
                        {application.active ? 'active' : 'inactive'}
                      </Badge>
                    ),
                  },
                  { label: 'Created', value: <span>{new Date(application.created_at).toLocaleString()}</span> },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-3">
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </TabsContent>

          <TabsContent value="api-keys" className="mt-4">
            <section className="surface-card p-6">
              <h2 className="font-heading text-base font-semibold tracking-tight">
                API credentials
              </h2>
              <div className="mt-4">
                {application.app_id ? (
                  <div className="space-y-4">
                    <ApiKeyDisplay
                      appId={application.app_id}
                      newKey={new_key}
                      rotateUrl={rotateUrl}
                    />
                    <p className="text-sm text-muted-foreground">
                      Use these credentials with our REST API.{' '}
                      <Link href="/docs" className="font-medium text-primary hover:underline">
                        View API Documentation
                      </Link>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No credentials found.</p>
                )}
              </div>
            </section>
          </TabsContent>

          <TabsContent value="events" className="mt-4">
            {recent_events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet.</p>
            ) : (
              <div className="surface-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Participant</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent_events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <span className="font-mono text-xs">{event.event_type}</span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {event.room_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {event.participant_id ?? '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(event.occurred_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  )
}
